import { useId } from "react";
import { Select } from "./Select";
import { Notice } from "./Notice";

import { newApprovalLevel, type ApprovalLevelDraft } from "./approvalsSupport";

/** Общий редактор для шаблона и ручного запуска согласования. */
export function ApprovalLevelsEditor({
  levels,
  onChange,
  people,
  disabled = false,
  errors = {},
}: {
  levels: ApprovalLevelDraft[];
  onChange: (levels: ApprovalLevelDraft[]) => void;
  people: readonly {
    id: string;
    display_name: string;
    substituted_by?: { id: string; display_name: string } | null;
  }[];
  disabled?: boolean;
  errors?: Record<string, string>;
}) {
  const prefix = useId();
  function update(index: number, patch: Partial<ApprovalLevelDraft>) {
    onChange(
      levels.map((level, position) =>
        position === index ? { ...level, ...patch } : level,
      ),
    );
  }
  function move(index: number, offset: number) {
    const next = [...levels];
    const other = index + offset;
    [next[index], next[other]] = [next[other]!, next[index]!];
    onChange(next);
  }
  return (
    <section className="approval-levels" aria-label="Уровни согласования">
      <h3>Уровни согласования</h3>
      {errors.steps ? <Notice>{errors.steps}</Notice> : null}
      {levels.map((level, index) => {
        const error = (field: string) => errors[`steps.${index}.${field}`];
        return (
          <fieldset
            key={level.key}
            disabled={disabled}
            className="approval-level"
          >
            <legend>Уровень {index + 1}</legend>
            <label className="field">
              <span>Режим</span>
              <Select
                value={level.mode}
                onChange={(event) =>
                  update(index, {
                    mode: event.target.value === "any" ? "any" : "all",
                  })
                }
              >
                <option value="all">Должны согласовать все</option>
                <option value="any">Достаточно одного</option>
              </Select>
            </label>
            <div className="approval-due">
              <label className="field">
                <span>Срок</span>
                <input
                  type="number"
                  min="1"
                  max="32767"
                  step="1"
                  value={level.amount}
                  onChange={(event) =>
                    update(index, { amount: event.target.value })
                  }
                  aria-describedby={`${prefix}-${index}-due`}
                />
              </label>
              <label className="field">
                <span>Единица срока</span>
                <Select
                  value={level.unit}
                  onChange={(event) =>
                    update(index, {
                      unit:
                        event.target.value === "hours"
                          ? "hours"
                          : "working_days",
                    })
                  }
                >
                  <option value="working_days">рабочих дней</option>
                  <option value="hours">часов</option>
                </Select>
              </label>
            </div>
            <small
              id={`${prefix}-${index}-due`}
              className={error("due.amount") ? "field-error" : "muted"}
              role={error("due.amount") ? "alert" : undefined}
            >
              {error("due.amount") ??
                (level.amount === ""
                  ? "Будет использован срок по умолчанию, заданный в настройках модуля"
                  : "Срок отсчитывается с открытия уровня")}
            </small>
            <div className="field">
              <span>Согласующие</span>
              <div
                className="approval-people"
                role="group"
                aria-label={`Согласующие уровня ${index + 1}`}
              >
                {people.map((person) => (
                  <label key={person.id} className="cp-check">
                    <input
                      type="checkbox"
                      checked={level.approver_ids.includes(person.id)}
                      disabled={
                        !level.approver_ids.includes(person.id) &&
                        level.approver_ids.length >= 10
                      }
                      onChange={(event) =>
                        update(index, {
                          approver_ids: event.target.checked
                            ? [...level.approver_ids, person.id]
                            : level.approver_ids.filter(
                                (id) => id !== person.id,
                              ),
                        })
                      }
                    />
                    <span>
                      {person.display_name}
                      {person.substituted_by ? (
                        <small className="approval-badge">
                          Отсутствует · замещает{" "}
                          {person.substituted_by.display_name}
                        </small>
                      ) : null}
                    </span>
                  </label>
                ))}
                {people.length === 0 ? <p>Нет доступных сотрудников</p> : null}
              </div>
              {people
                .filter(
                  (person) =>
                    level.approver_ids.includes(person.id) &&
                    person.substituted_by,
                )
                .map((person) => (
                  <small key={person.id}>
                    Назначение получит {person.substituted_by!.display_name}
                  </small>
                ))}
              {error("approver_ids") ? (
                <small role="alert" className="field-error">
                  {error("approver_ids")}
                </small>
              ) : null}
            </div>
            <div className="approval-actions">
              <button
                className="button button--secondary button--compact"
                type="button"
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                Выше
              </button>
              <button
                className="button button--secondary button--compact"
                type="button"
                disabled={index === levels.length - 1}
                onClick={() => move(index, 1)}
              >
                Ниже
              </button>
              <button
                className="button button--text"
                type="button"
                onClick={() =>
                  onChange(levels.filter((_, position) => position !== index))
                }
              >
                Удалить уровень
              </button>
            </div>
          </fieldset>
        );
      })}
      <button
        className="button button--secondary"
        type="button"
        disabled={disabled || levels.length >= 10}
        onClick={() => onChange([...levels, newApprovalLevel()])}
      >
        Добавить уровень
      </button>
    </section>
  );
}
